/**
 * Caption layout for the help video player: at most two short lines in small letters on a
 * dark backing, so the caption stays distinct without covering much of the screen. Long
 * voiceover cues are split into lines, grouped into pages of up to two lines that follow
 * each other across the cue's time.
 */

/** Most caption lines on screen at once. */
export const CAPTION_MAX_LINES = 2;

/** Caption font size for a player of `width` CSS pixels: small, but readable. */
export function captionFontPx(width: number): number {
  return Math.round(Math.min(13, Math.max(10, width * 0.014)));
}

/** About how many characters fit on one caption line at that width and font size. */
export function captionMaxChars(width: number, fontPx: number): number {
  return Math.max(24, Math.floor((width * 0.8) / (fontPx * 0.55)));
}

/** Splits `text` at word boundaries into lines of at most `maxChars` (a longer word stays whole). */
export function splitCaptionLines(text: string, maxChars: number): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    if (line && line.length + 1 + word.length > maxChars) {
      lines.push(line);
      line = word;
    } else {
      line = line ? `${line} ${word}` : word;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/** Groups lines into pages of at most `perPage` lines (two by default). */
export function captionPages(lines: string[], perPage = CAPTION_MAX_LINES): string[][] {
  const pages: string[][] = [];
  for (let i = 0; i < lines.length; i += perPage) pages.push(lines.slice(i, i + perPage));
  return pages;
}

/**
 * The page to show at `time` for a cue running from `start` to `end`: each page gets a
 * share of the cue's time in proportion to its length.
 */
export function captionPageAt(pages: string[][], start: number, end: number, time: number): string[] {
  if (pages.length === 0) return [];
  if (pages.length === 1 || end <= start) return pages[0];
  const size = (page: string[]) => page.reduce((sum, line) => sum + line.length, 0);
  const total = pages.reduce((sum, page) => sum + size(page), 0);
  const elapsed = Math.min(1, Math.max(0, (time - start) / (end - start))) * total;
  let passed = 0;
  for (const page of pages) {
    passed += size(page);
    if (elapsed < passed) return page;
  }
  return pages[pages.length - 1];
}

/** Share of the player's height, from the bottom, where the native controls appear on hover. */
export const CONTROLS_ZONE = 0.3;

/**
 * Captions sit at the bottom unless the viewer's pointer is over the controls zone, or
 * the cue itself asks for the top (VTT `line:0`: the action in the video is at the bottom).
 */
export function captionAtTop(cueWantsTop: boolean, pointerInControlsZone: boolean): boolean {
  return cueWantsTop || pointerInControlsZone;
}
